// Readers for `redesign_provenance` — the one new top-level key on
// GET /save-optimized-version/{id}?engines=… (alongside workflow/gateways).
//
// It answers a question the save payload alone cannot: of everything the analyst
// just approved on the RL/BPR tab, how much of it is actually in this payload?
// A process version can only carry structure — tasks, order, gateways. A heuristic
// that changes a duration or reshuffles a RACI assignment has no field to land in,
// so it silently evaporates unless something says so out loud. That "something" is
// the confirm step (buildSaveSummary) and the adopt list (readAdoptableHeuristics).
//
// Field-name tolerance below is deliberate. The counts in `structural_coverage` are
// the engine's own bookkeeping and the exact key names are not pinned by contract,
// so each count is read through a list of plausible aliases and falls back to a
// derivation from data we DO hold (the applied codes and the adopt list). A summary
// that reads "Saving 3 of 7" from the wrong key would be worse than one derived.

export const ENGINE_LABELS = { agent: "Agent", rl: "RL", bpr: "BPR" };

export function readRedesignProvenance(payload) {
  return payload?.redesign_provenance || null;
}

/** Engines the backend actually composed, always including the agent base. */
export function readProvenanceEngines(prov) {
  const list = Array.isArray(prov?.engines) ? prov.engines.filter(Boolean) : [];
  return list.length ? list : ["agent"];
}

export function readEngineDetail(prov, engine) {
  return prov?.engine_detail?.[engine] || null;
}

const asArray = (v) => (Array.isArray(v) ? v : []);
// Some of these arrive as a list of ids/pairs and some as a plain count.
const countOf = (v) => (Array.isArray(v) ? v.length : Number.isFinite(Number(v)) ? Number(v) : 0);

function firstNumber(obj, keys) {
  if (!obj) return null;
  for (const k of keys) {
    const n = Number(obj[k]);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * The adopt list — heuristics an engine applied (or merely suggests) that have no
 * structural field to save into. Rows arrive render-ready: `name`, `impact_note`
 * and `impact_computable` are resolved server-side, so nothing here re-derives them.
 *
 * The two statuses must NOT be collapsed into one list. `applied_not_persistable`
 * means the engine really did this and the payload just can't carry it; showing it
 * as a suggestion demotes RL's work to a guess. `recommended` means nobody applied
 * it; showing it as applied credits the agent with work it never did.
 */
export function readAdoptableHeuristics(prov) {
  return asArray(prov?.adoptable_heuristics);
}

export function isEngineApplied(row) {
  return row?.status === "applied_not_persistable";
}

/** "Applied by RL" vs "Suggested" — see isEngineApplied for why they differ. */
export function adoptRowLabel(row) {
  const src = ENGINE_LABELS[row?.source] || row?.source || "the engine";
  return isEngineApplied(row) ? `Applied by ${src}` : "Suggested";
}

/**
 * Per-engine split of "how much of what you approved is structure we can save".
 *
 * `total` is every heuristic the engine applied; `structural` is the subset that
 * landed in the payload. When structural_coverage carries the numbers we use them;
 * otherwise structural = total − (this engine's applied-but-not-persistable rows),
 * which is the same quantity counted from the other side.
 */
export function readEngineCoverage(prov, engine) {
  const detail = readEngineDetail(prov, engine);
  if (!detail) return null;
  const cov = detail.structural_coverage || null;

  const applied = asArray(detail.applied_heuristic_codes);
  const notPersistable = readAdoptableHeuristics(prov).filter(
    (r) => r.source === engine && isEngineApplied(r),
  );

  const total =
    firstNumber(cov, ["total", "total_count", "applied", "applied_count", "considered"]) ??
    (applied.length || notPersistable.length);
  const structural =
    firstNumber(cov, ["structural", "structural_count", "persisted", "persistable", "expressible"]) ??
    Math.max(0, total - notPersistable.length);

  return {
    engine,
    label: ENGINE_LABELS[engine] || engine,
    status: detail.status || "ok",
    failed: detail.status === "failed",
    resultReused: detail.result_reused !== false,
    reuseNote: detail.reuse_note || "",
    goal: detail.goal || null,
    seed: detail.seed ?? null,
    total,
    structural,
    nonStructural: Math.max(0, total - structural),
  };
}

/**
 * Everything the confirm step needs, in one pass.
 *
 * `divergences` are the cases where what the analyst saw on the engine tab differs
 * from what this save will write. Each one is surfaced individually rather than
 * rolled into a count, because they have different causes and different remedies.
 */
export function buildSaveSummary(prov) {
  if (!prov) return null;

  const engines = readProvenanceEngines(prov).filter((e) => e !== "agent");
  const coverage = engines.map((e) => readEngineCoverage(prov, e)).filter(Boolean);

  const applied = prov.structure_applied || {};
  const eliminated = countOf(applied.eliminated_task_ids);
  const engineEliminations = countOf(applied.engine_eliminations_accepted);
  const rejectedEliminations = countOf(applied.rejected_engine_eliminations);
  const acceptedParallel = countOf(applied.accepted_parallel);
  const rejectedParallel = countOf(applied.rejected_parallel);
  const notExpressible = countOf(applied.engine_pairs_not_expressible);

  const divergences = [];
  if (rejectedParallel > 0) {
    divergences.push({
      key: "rejected_parallel",
      tone: "warn",
      title: `${rejectedParallel} parallel split${rejectedParallel === 1 ? "" : "s"} proposed but not applied`,
      detail:
        "These were re-scored against the current process and could not be shown to improve it, so they were dropped rather than saved.",
    });
  }
  if (rejectedEliminations > 0) {
    divergences.push({
      key: "rejected_engine_eliminations",
      tone: "warn",
      title: `${rejectedEliminations} engine elimination${rejectedEliminations === 1 ? "" : "s"} not applied`,
      detail:
        "The combined set could not be proven an improvement over the agent's own, so the agent's elimination set stands. The tasks the engine wanted removed stay in the saved version.",
    });
  }
  if (notExpressible > 0) {
    divergences.push({
      key: "engine_pairs_not_expressible",
      tone: "warn",
      title: `${notExpressible} parallel pair${notExpressible === 1 ? "" : "s"} could not be expressed`,
      detail:
        "A process version stores tasks in one flat order, so only adjacent tasks can be split into a parallel branch. These pairs are not adjacent in the stored order.",
    });
  }
  for (const c of coverage) {
    if (c.failed) {
      divergences.push({
        key: `failed_${c.engine}`,
        tone: "warn",
        title: `${c.label} was unreachable and contributed nothing`,
        detail:
          c.reuseNote ||
          "The external model could not be reached. This save is still valid — the agent produced it — but it contains none of this engine's redesign.",
      });
    } else if (!c.resultReused) {
      divergences.push({
        key: `rerun_${c.engine}`,
        tone: "warn",
        title: `${c.label} was re-run rather than reused`,
        detail:
          c.reuseNote ||
          "The stored result could not be reused (references are kept for one hour), so the engine ran again. What is being saved may differ from the redesign you reviewed.",
      });
    }
  }

  const structuralTotal = coverage.reduce((s, c) => s + c.structural, 0);
  const nonStructuralTotal = coverage.reduce((s, c) => s + c.nonStructural, 0);
  const structureCount = eliminated + acceptedParallel;

  // Selecting an engine can legitimately change nothing — RL may only propose splits
  // the agent already found, or none may survive validation. That is a correct
  // outcome, and the copy has to say so instead of implying the engine was ignored.
  const noStructuralChange = coverage.length > 0 && structuralTotal === 0 && structureCount === 0;

  // The analyst's own decisions, carried into the confirm step. The two that matter
  // there are the ones the payload cannot show on its own: what they removed (a
  // declined row is absent from the version and from applied_heuristics, so nothing
  // else in the confirm body would mention it) and what they added against the
  // measurement's advice. Both are theirs to reconsider at the last gate.
  const appliedRows = readAppliedHeuristics(prov);
  const selection = {
    note: prov.selection?.note || "",
    selectedKeys: asArray(prov.selection?.selected_keys),
    unmatchedKeys: asArray(prov.selection?.unmatched_keys),
    declined: readDeclinedHeuristics(prov),
    uncredited: appliedRows.filter(isUncreditedAdoption),
  };

  return {
    coverage,
    counts: {
      eliminated,
      engineEliminations,
      rejectedEliminations,
      acceptedParallel,
      rejectedParallel,
      notExpressible,
      structuralTotal,
      nonStructuralTotal,
    },
    divergences,
    noStructuralChange,
    adoptable: readAdoptableHeuristics(prov),
    selection,
  };
}

/** The one-line headline of the confirm step, e.g. "Saving 3 of 7 RL heuristics…". */
export function summarySentence(summary) {
  if (!summary) return "";
  const { counts, coverage } = summary;
  const scope = coverage.length
    ? coverage.map((c) => `${c.structural} of ${c.total} ${c.label}`).join(" and ")
    : "";
  const landed = [
    counts.eliminated ? `${counts.eliminated} task${counts.eliminated === 1 ? "" : "s"} eliminated` : null,
    counts.acceptedParallel
      ? `${counts.acceptedParallel} parallel split${counts.acceptedParallel === 1 ? "" : "s"} accepted`
      : null,
  ].filter(Boolean);

  const head = scope
    ? `Saving ${scope} heuristic${counts.structuralTotal === 1 ? "" : "s"} as process structure`
    : "Saving the agent's redesign as process structure";
  const body = landed.length ? `: ${landed.join(", ")}.` : ": no structural change.";
  const tail = counts.nonStructuralTotal
    ? ` ${counts.nonStructuralTotal} heuristic${counts.nonStructuralTotal === 1 ? "" : "s"} change duration${counts.nonStructuralTotal === 1 ? "" : "s"} or RACI assignments, which a process version can't carry.`
    : "";
  return `${head}${body}${tail}`;
}

// ---------------------------------------------------------------------------
// The save checklist — `applied_heuristics` and the `adopt` round-trip.
//
// The adopt list above answers "what can this version NOT carry?". This section
// answers the other half: of the structure it CAN carry, which of it does the
// analyst actually want written? Those are different questions with different
// consequences — the first is a disclosure, the second is an instruction — so they
// are read through different functions and rendered as different surfaces.
//
// The row shape is the backend's, unchanged, and every justification field on it
// (measured_verdict, rationale, basis, not_saved_reason) is displayed verbatim.
// Nothing here paraphrases an engine's reasoning: the row is the engine's account
// of its own work, and a summary of it written on this side would be our opinion
// wearing the engine's name.

/** The two heuristics a process version can physically carry. */
export const STRUCTURAL_HEURISTIC_CODES = new Set(["parallelism", "activity-elimination"]);

/**
 * status → badge. `tone` maps to the palette already used across the Save tab
 * (green = in the payload, amber = wanted but not written, muted = declined).
 *
 * `counted` marks the statuses that mean "this is on by default" — the pre-check
 * rule for the first load. It is deliberately separate from `tickable`:
 * applied_not_persistable rows are on, and stay on, without being switchable.
 */
export const HEURISTIC_STATUS = {
  saved_as_structure: {
    tone: "ok",
    label: "Saved as structure",
    blurb: "In the workflow and gateways this save writes.",
    counted: true,
  },
  applied_not_persistable: {
    tone: "neutral",
    label: "Applied — not persistable",
    blurb: "The engine made this change; a process version has no field to store it.",
    counted: true,
  },
  not_selected: {
    tone: "muted",
    label: "Declined",
    blurb: "You unticked this, so it is not written.",
    counted: false,
  },
  rejected_by_measurement: {
    tone: "warn",
    label: "Not written",
    blurb: "Could not be written into the structure.",
    counted: false,
  },
  not_representable: {
    tone: "warn",
    label: "Not representable",
    blurb: "This trace cannot be expressed as a gateway.",
    counted: false,
  },
  proposed_to_structure: {
    tone: "warn",
    label: "Outcome untraceable",
    blurb: "Proposed to the structure, but the result could not be traced back.",
    counted: false,
  },
};

export function readHeuristicStatus(row) {
  const key = row?.status || "";
  return (
    HEURISTIC_STATUS[key] || {
      tone: "warn",
      label: String(key || "unknown").replace(/_/g, " "),
      blurb: "",
      counted: false,
    }
  );
}

/** The list to render: agent rows first, then RL/BPR, minus anything unticked. */
export function readAppliedHeuristics(prov) {
  return asArray(prov?.applied_heuristics);
}

/** Includes the rows the analyst unticked — the only place they still appear. */
export function readStructuralHeuristics(prov) {
  return asArray(prov?.structural_heuristics);
}

/** The declined rows, for the "not being saved" section under the checklist. */
export function readDeclinedHeuristics(prov) {
  return readStructuralHeuristics(prov).filter((r) => r?.status === "not_selected");
}

/**
 * Can the analyst switch this row?
 *
 * Every engine row the analyst saw applied on a Redesign tab is switchable here,
 * whatever its code or status. The tick is one decision — "keep this" — and what
 * that decision produces depends on the row, which is what carriesAsStructure
 * below distinguishes: a structural row is written into the workflow, and a
 * non-persistable one is recorded against the saved redesign scenario. Neither is
 * a reason to take the control away; the analyst owns both outcomes.
 *
 * Agent rows are the one exception, and not as a policy choice: `adopt` is the
 * engine opt-in list and the endpoint does not accept agent keys, so an agent
 * checkbox could not do anything except land in `unmatched_keys` and block the
 * save. See agentAlsoProposes for the case where that limit becomes visible.
 */
export function isHeuristicTickable(row) {
  if (!row?.selection_key) return false;
  return row.source !== "agent";
}

/** Does ticking this write structure, or only record the decision? */
export function carriesAsStructure(row) {
  return !!row && row.status !== "applied_not_persistable" && STRUCTURAL_HEURISTIC_CODES.has(row.code);
}

/** Why the checkbox is disabled, in the analyst's terms. Null when it isn't. */
export function tickDisabledReason(row) {
  if (!row || isHeuristicTickable(row)) return null;
  if (row.source === "agent")
    return "The agent is the base this version is built from — its contributions are written by the same call, so there is nothing to opt into.";
  return "This row has no selection key, so there is nothing to send back.";
}

/** What a tick on this row actually does, said on the row itself. */
export function tickEffectNote(row) {
  if (!isHeuristicTickable(row)) return "";
  if (carriesAsStructure(row))
    return "Ticked, this is written into the saved workflow and gateways.";
  return "A process version has no field for this one, so ticking it records it against the saved redesign scenario instead of changing the workflow. Unticking drops it from the save entirely.";
}

/**
 * The keys that are ON for a freshly loaded (adopt-free) response.
 *
 * Agent rows are excluded even though they read saved_as_structure: `adopt` is the
 * engine opt-in list, and sending a key the parameter does not govern is how you
 * end up in `unmatched_keys` warning about a divergence that never happened.
 */
export function defaultAdoptKeys(prov) {
  return readAppliedHeuristics(prov)
    .filter((r) => r?.selection_key && r.source !== "agent" && readHeuristicStatus(r).counted)
    .map((r) => r.selection_key);
}

const idKey = (ids) =>
  asArray(ids)
    .map((v) => String(v))
    .sort()
    .join("|");

/**
 * Which engine rows describe a change the AGENT independently proposed too.
 *
 * This is the one place the checklist can look like it is lying. In a save where
 * the agent's own detector found the same 993+994 pair as RL, unticking every
 * engine parallelism row leaves the gateway in the payload — correctly, because the
 * agent is the always-on base and `adopt` does not govern it. On screen that reads
 * as a dead checkbox.
 *
 * The backend's `also_proposed_by` is preferred when present. Until it ships, the
 * same fact is derivable here: an agent row that is saved_as_structure, shares the
 * code, and covers the same tasks IS the reason the structure survives. Matching on
 * `structural_task_ids` (order-insensitive — the writer may reorder a pair) rather
 * than on `task_ids` matters: the claim being made is about what got written.
 */
export function agentAlsoProposes(prov, row) {
  const declared = asArray(row?.also_proposed_by);
  if (declared.length) return declared.includes("agent");
  if (!row?.code || row.source === "agent") return false;

  const mine = idKey(row.structural_task_ids?.length ? row.structural_task_ids : row.task_ids);
  if (!mine) return false;
  return readAppliedHeuristics(prov).some(
    (r) =>
      r?.source === "agent" &&
      r.code === row.code &&
      r.status === "saved_as_structure" &&
      idKey(r.structural_task_ids?.length ? r.structural_task_ids : r.task_ids) === mine,
  );
}

/**
 * A saved parallelism row whose gateway the what-if could not credit with any
 * cycle-time gain, yet which the analyst's tick applied anyway.
 *
 * Both halves are required. `measured_improvement === false` alone is the engine
 * reporting a flat measurement; paired with `analyst_adopted` it becomes a decision
 * the analyst made against the measurement, and that is the version of the fact
 * they need to see on the row rather than discover in a report later.
 */
export function isUncreditedAdoption(row) {
  const g = row?.saved_gateway;
  return !!g && g.measured_improvement === false && g.analyst_adopted === true;
}

/**
 * Warnings that belong ABOVE the checklist because they invalidate it rather than
 * annotate it — the list on screen may no longer describe the redesign the analyst
 * reviewed. Engine failure and non-reuse are reported per engine, so an analyst
 * looking at an Agent+RL+BPR save can tell which half of it moved.
 */
export function readSelectionWarnings(prov) {
  if (!prov) return [];
  const out = [];
  const unmatched = asArray(prov.selection?.unmatched_keys);
  if (unmatched.length) {
    out.push({
      key: "unmatched_keys",
      tone: "error",
      title: `${unmatched.length} of your selections no longer exist in this redesign`,
      detail:
        "The engine was re-run, so the rows you ticked are not the rows being saved. Reload the save tab and make the selection again before saving.",
      keys: unmatched,
    });
  }
  for (const engine of readProvenanceEngines(prov)) {
    if (engine === "agent") continue;
    const d = readEngineDetail(prov, engine);
    if (!d) continue;
    const label = ENGINE_LABELS[engine] || engine;
    if (d.status === "failed") {
      out.push({
        key: `failed_${engine}`,
        tone: "warn",
        title: `${label} contributed nothing to this save`,
        detail:
          d.note ||
          d.reuse_note ||
          "The engine could not be reached. The save is still valid — the agent produced it — but it carries none of this engine's redesign.",
      });
    } else if (d.result_reused === false) {
      out.push({
        key: `rerun_${engine}`,
        tone: "warn",
        title: `${label} was re-run rather than reused`,
        detail:
          d.reuse_note ||
          "The stored result could not be reused, so the engine ran again. What is being saved may differ from the redesign you reviewed.",
      });
    }
  }
  return out;
}

/** Rows grouped by source, in the order the backend hands them over. */
export function groupHeuristicsBySource(rows) {
  const order = [];
  const bucket = new Map();
  for (const row of asArray(rows)) {
    const src = row?.source || "agent";
    if (!bucket.has(src)) {
      bucket.set(src, []);
      order.push(src);
    }
    bucket.get(src).push(row);
  }
  return order.map((source) => ({
    source,
    label: ENGINE_LABELS[source] || source,
    rows: bucket.get(source),
  }));
}

/** FBPM category, or the honest placeholder when the CMS catalog was unreachable. */
export function heuristicCategoryLabel(row) {
  const c = String(row?.category || "").trim();
  return c ? c.replace(/_/g, " ") : "Uncategorised";
}

/** The task ids to show: what was WRITTEN when we know it, else what was named. */
export function heuristicTaskIds(row) {
  const structural = asArray(row?.structural_task_ids);
  return structural.length ? structural : asArray(row?.task_ids);
}

// ---------------------------------------------------------------------------
// Engine composition marker on a saved TO-BE scenario.
//
// The Redesign Scenarios tab is a read-only view over stored records, and the only
// field that travels with a scenario is its free-text description. So the engine
// set is written into that text as one fixed sentence and parsed back out here,
// rather than left for a reader to spot in the prose. An Agent+RL card and an
// Agent+BPR card otherwise look identical, and the numbers on them are not
// interchangeable — which engines produced a redesign is the first thing a reader
// needs, so it has to render as a badge, not as a clause halfway through a summary.
//
// Keep COMPOSED_WITH_RE in step with formatEngineComposition: the parser is what
// keeps already-saved cards readable, so the written form can never drift from it.

const COMPOSED_WITH_RE = /\s*Composed with:\s*([^.]+)\.\s*/;

/** The sentence embedded in a saved TO-BE scenario's description. */
export function formatEngineComposition(engineLabels) {
  const list = (engineLabels || []).filter(Boolean);
  return `Composed with: ${(list.length ? list : ["Agent"]).join(" + ")}.`;
}

/**
 * Split a stored description into its engine list and the remaining prose.
 *
 * A record saved before this marker existed returns `engines: null` — not
 * `["Agent"]`. Those saves were agent-only in fact, but the card should say
 * "unlabelled" rather than assert a composition that was never recorded.
 */
export function parseEngineComposition(description) {
  const text = String(description || "");
  const m = text.match(COMPOSED_WITH_RE);
  if (!m) return { engines: null, rest: text.trim() };
  return {
    engines: m[1].split("+").map((s) => s.trim()).filter(Boolean),
    rest: text.replace(COMPOSED_WITH_RE, " ").replace(/\s+/g, " ").trim(),
  };
}

/** Chip colours per engine, so Agent+RL and Agent+BPR are distinguishable at a glance. */
export const ENGINE_CHIP_STYLE = {
  Agent: { color: "var(--teal-ink-deep)", background: "var(--teal-tint-100)", border: "1px solid var(--teal-tint-200)" },
  RL: { color: "var(--violet-ink-800)", background: "var(--violet-tint-100)", border: "1px solid var(--violet-tint-300)" },
  BPR: { color: "var(--blue-ink-700)", background: "var(--blue-tint-100)", border: "1px solid var(--blue-tint-200)" },
};
